import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import './AIDoctorReport.css';

const floatingIcons = [
    { icon: '∑', top: '10%', left: '15%', delay: '0s' },
    { icon: '🧪', top: '20%', left: '80%', delay: '2s' },
    { icon: '⚛', top: '70%', left: '10%', delay: '4s' },
    { icon: '📜', top: '80%', left: '85%', delay: '1s' },
    { icon: 'A', top: '15%', left: '50%', delay: '3s' },
    { icon: 'π', top: '60%', left: '25%', delay: '5s' },
    { icon: '🧬', top: '40%', left: '90%', delay: '2.5s' },
];

const AIDoctorReport = () => {
    const { t } = useTranslation(['reports', 'common', 'test', 'navigation']);
    const location = useLocation();

    const navigate = useNavigate();
    const state = React.useMemo(() => location.state || {}, [location.state]);

    const [loading, setLoading] = useState(true);
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState(null);
    const [showExceededModal, setShowExceededModal] = useState(false);
    const apiCalledRef = useRef(false); // prevent double-fire

    useEffect(() => {
        // Guard: only call once (prevents double-fire from re-renders)
        if (apiCalledRef.current) return;
        apiCalledRef.current = true;

        // Review mode: fetch stored analysis from DB by test_id
        if (state.mode === 'review') {
            if (!state.test_id) {
                // No test_id stored — show loading=false, analysis=null (fallback card renders)
                setLoading(false);
                return;
            }
            const fetchStored = async () => {
                try {
                    const res = await fetch(`${API_BASE_URL}get_analysis.php?test_id=${state.test_id}`, { credentials: 'include' });
                    const data = await res.json();
                    if (data.status === 'success') {
                        setAnalysis(data.analysis);
                    }
                    // if not_found, analysis stays null — fallback card renders
                } catch (err) {
                    console.error('Could not load stored analysis:', err);
                } finally {
                    setLoading(false);
                }
            };
            fetchStored();
            return;
        }

        if (!state.questions || !state.answers) {
            setError(t('reports:reports.aiDoctor.unavailable', "No test data found. Please complete a test first."));
            setLoading(false);
            return;
        }


        const userId = parseInt(localStorage.getItem('userId') || '0', 10) || null;

        const fetchAnalysis = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}generate_analysis.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        board: state.board,
                        class: state.class,
                        subject: state.subject,
                        chapter: state.chapter,
                        questions: state.questions,
                        answers: state.answers,
                        user_id: userId,               // from localStorage — real user
                        test_id: state.test_id,        // from submit_test response
                        subject_id: state.subject_id,
                        chapter_id: state.chapter_id,
                        class_id: parseInt(localStorage.getItem('classId') || '0', 10) || null,
                    })
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setAnalysis(data.analysis);
                } else {
                    throw new Error(data.message || "Failed to generate AI analysis");
                }
            } catch (err) {
                console.error("Analysis Fetch Error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalysis();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [loadingStep, setLoadingStep] = useState(0);
    const analysisSteps = useMemo(() => [
        t('reports:reports.aiDoctor.steps.analyzing', "ANALYZING ANSWERS"),
        t('reports:reports.aiDoctor.steps.identifying', "IDENTIFYING GAPS"),
        t('reports:reports.aiDoctor.steps.mastery', "CALCULATING MASTERY"),
        t('reports:reports.aiDoctor.steps.plan', "GENERATING PLAN")
    ], [t]);


    useEffect(() => {
        if (!loading) return;
        const interval = setInterval(() => {
            setLoadingStep(prev => (prev + 1) % analysisSteps.length);
        }, 1500);
        return () => clearInterval(interval);
    }, [loading, analysisSteps.length]);

    if (loading) {
        return (
            <div className="dr-page-container dr-loading-mode">
                <div className="dr-background-glows">
                    <div className="dr-glow-1"></div>
                    <div className="dr-glow-2"></div>
                </div>

                <div className="dr-floating-elements">
                    {floatingIcons.map((item, index) => (
                        <div key={index} className="dr-floating-icon"
                            style={{ top: item.top, left: item.left, animationDelay: item.delay }}>
                            {item.icon}
                        </div>
                    ))}
                </div>

                <main className="dr-main-content no-sidebar">
                    <div className="dr-loading-wrapper">
                        <div className="dr-visual-container">
                            <div className="dr-energy-ring"></div>
                            <div className="dr-hologram-wrapper">
                                <div className="dr-hologram-icon">Ai</div>
                            </div>
                        </div>

                        <div className="dr-loading-text-block">
                            <h2 className="dr-neon-text">{t('reports:reports.aiDoctor.analyzing', 'SkillTherapy AI is Analyzing Your Performance...')}</h2>
                            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '2rem' }}>
                                {t('reports:reports.aiDoctor.scanning', 'Scanning mistake patterns, calculating concept mastery, and preparing your 7-day plan.')}
                            </p>
                            <div className="dr-scan-line"></div>
                        </div>


                        <div className="dr-loading-steps">
                            {analysisSteps.map((step, index) => (
                                <div key={index} className={`dr-step ${index === loadingStep ? 'active' : ''}`}>
                                    {step}
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    const handleRetake = async () => {
        const userId = localStorage.getItem('userId');
        try {
            const res = await fetch(`${API_BASE_URL}check_package_status.php?user_id=${userId}`, { credentials: 'include' });
            const data = await res.json();

            if (data.status === 'success') {
                if (data.exceeded) {
                    setShowExceededModal(true);
                } else {
                    navigate('/loader', {
                        state: {
                            board: state.board,
                            class: state.class,
                            subject: state.subject,
                            subject_id: state.subject_id,
                            chapter: state.chapter,
                            chapter_id: state.chapter_id,
                        }
                    });
                }
            } else {
                alert("Unable to verify account status. Please try again.");
            }
        } catch (err) {
            console.error("Error checking package status:", err);
            alert("Connection error. Please check your internet and try again.");
        }
    };

    // ── Review Mode Fallback: show basic card only when no stored analysis found ──
    // When analysis IS loaded from get_analysis.php, this block is skipped and
    // the full report renders below.
    if (state.mode === 'review' && !analysis) {
        const score = state.score_percentage ?? 0;
        const correct = state.correct_answers ?? 0;
        const total = state.total_questions ?? 0;
        
        const gradeKey = score >= 80 ? 'excellent' : score >= 60 ? 'good' : score >= 40 ? 'average' : 'needsWork';
        const grade = t(`reports:reports.aiDoctor.grades.${gradeKey}`);
        const gradeColor = score >= 80 ? '#22c55e' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444';


        return (
            <div className="dr-page-container dr-report-view">
                <div className="dr-background-glows">
                    <div className="dr-glow-1"></div>
                    <div className="dr-glow-2"></div>
                </div>
                <div className="dr-floating-elements">
                    {floatingIcons.map((item, index) => (
                        <div key={index} className="dr-floating-icon"
                            style={{ top: item.top, left: item.left, animationDelay: item.delay }}>
                            {item.icon}
                        </div>
                    ))}
                </div>

                <main className="dr-main-content no-sidebar">
                    <div className="dr-content-wrapper">
                        {/* Hero */}
                        <div className="dr-hero">
                            <div className="dr-hero-left">
                                <div className="dr-status-badge">{t('reports:reports.aiDoctor.statusReview', 'Test Completed • Review')}</div>
                                <h1>{t('reports:reports.aiDoctor.chapterScore', { score, defaultValue: `Chapter Score: ${score}%` })}</h1>
                                <p className="dr-meta">{state.board} • {state.subject} • {state.chapter}</p>
                            </div>
                            <div className="dr-doctor-shield">
                                <div className="shield-icon">🧠</div>
                                <div className="shield-text">{t('reports:reports.aiDoctor.analyzeMode', 'ANALYZE MODE')}</div>
                            </div>
                        </div>


                        {/* Summary Card */}
                        <div className="dr-card" style={{ marginTop: '2rem', padding: '2.5rem' }}>
                            <h3 style={{ marginBottom: '2rem', fontSize: '1.2rem' }}>📊 {t('reports:reports.testReport.summary', 'Test Summary')}</h3>
                            <div className="dr-stats-row">
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-correct">{correct}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.correct', 'Correct')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-wrong">{total - correct}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.aiDoctor.grades.needsWork', 'Needs Work')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val" style={{ color: 'var(--neon-blue)' }}>{total}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.totalQuestions', 'Total Questions')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val" style={{ color: gradeColor }}>{grade}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.grade', 'Grade')}</span>
                                </div>
                            </div>


                            {/* Score bar */}
                            <div style={{ marginTop: '2rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
                                    <span>{t('reports:reports.testReport.scoreProgress', 'Score Progress')}</span>
                                    <span>{score}%</span>
                                </div>

                                <div className="dr-meter-track">
                                    <div className="dr-meter-fill" style={{ width: `${score}%`, background: gradeColor }}></div>
                                </div>
                            </div>

                            <div style={{ marginTop: '1.5rem', padding: '1rem 1.25rem', background: 'rgba(0,212,255,0.05)', borderRadius: '14px', border: '1px solid rgba(0,212,255,0.1)' }}>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    💡 <strong>{t('reports:reports.aiDoctor.note', 'Note')}:</strong> {t('reports:reports.aiDoctor.noteText')}
                                </p>
                            </div>

                        </div>

                        {/* Actions */}
                        <div className="dr-footer-actions" style={{ marginTop: '2rem' }}>
                            <button
                                onClick={handleRetake}
                                className="dr-btn-primary"
                            >
                                🔄 {t('reports:reports.aiDoctor.retake', 'Re-take Test')}
                            </button>
                            <button onClick={() => navigate('/subjects')} className="dr-btn-outline">
                                ← {t('reports:reports.aiDoctor.backToSubjects', 'Back to Subjects')}
                            </button>

                        </div>
                    </div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dr-page-container dr-loading-mode">
                <main className="dr-main-content no-sidebar">

                    {/* Background Visuals for Error State */}
                    <div className="dr-background-glows">
                        <div className="dr-glow-1"></div>
                        <div className="dr-glow-2"></div>
                    </div>
                    <div className="dr-floating-elements">
                        {floatingIcons.map((item, index) => (
                            <div
                                key={index}
                                className="dr-floating-icon"
                                style={{ top: item.top, left: item.left, animationDelay: item.delay }}
                            >
                                {item.icon}
                            </div>
                        ))}
                    </div>

                    <div className="dr-content-wrapper">
                        <div className="dr-error-card" style={{ textAlign: 'center', padding: '4rem', zIndex: 10, position: 'relative' }}>
                            <h2 style={{ color: '#fff', marginBottom: '1rem' }}>⚠️ {t('reports:reports.aiDoctor.unavailable', 'Analysis Unavailable')}</h2>
                            <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem' }}>{error}</p>
                            <button onClick={() => navigate('/dashboard')} className="dr-btn-primary">{t('reports:reports.aiDoctor.backToDashboard', 'Back to Dashboard')}</button>
                        </div>

                    </div>
                </main>
            </div>
        );
    }

    const {
        score_percent = 0,
        accuracy = { correct: 0, wrong: 0, skipped: 0 },
        topic_weakness = [],
        ai_insights = [],
        recommended_practice = [],
        seven_day_plan = [],
        confidence_meter = 0,
        mistake_reason_analysis = {},
        difficulty_performance = { easy: { correct: 0, wrong: 0 }, medium: { correct: 0, wrong: 0 }, hard: { correct: 0, wrong: 0 } }
    } = analysis || {};

    // Fallback mapping if AI returns a different structure (e.g. nested in student_report)
    if (analysis?.student_report) {
        const sr = analysis.student_report;
        if (topic_weakness.length === 0 && sr.personalized_feedback?.areas_for_improvement) {
            // Map areas_for_improvement to topic_weakness format if missing
            sr.personalized_feedback.areas_for_improvement.forEach(item => {
                topic_weakness.push({ topic: item, status: 'Needs Work' });
            });
        }
        if (ai_insights.length === 0 && sr.personalized_feedback?.strengths) {
            ai_insights.push(...sr.personalized_feedback.strengths);
        }
        if (recommended_practice.length === 0 && sr.personalized_feedback?.study_recommendations) {
            sr.personalized_feedback.study_recommendations.forEach(item => {
                recommended_practice.push({ type: 'Study', priority: 'High', task: item });
            });
        }
    }

    return (
        <div className="dr-page-container dr-report-view">
            {/* Background Animated Glows and Icons */}
            <div className="dr-background-glows">
                <div className="dr-glow-1"></div>
                <div className="dr-glow-2"></div>
            </div>
            <div className="dr-floating-elements">
                {floatingIcons.map((item, index) => (
                    <div
                        key={index}
                        className="dr-floating-icon"
                        style={{ top: item.top, left: item.left, animationDelay: item.delay }}
                    >
                        {item.icon}
                    </div>
                ))}
            </div>

            <main className="dr-main-content no-sidebar">
                <div className="dr-content-wrapper">
                    {/* Hero Section */}
                    <div className="dr-hero">
                        <div className="dr-hero-left">
                            <div className="dr-status-badge">{t('reports:reports.aiDoctor.statusFinalized', 'Diagnostic Finalized')}</div>
                            <h1>{t('reports:reports.aiDoctor.chapterMastery', { score: score_percent, defaultValue: `Chapter Mastery: ${score_percent}%` })}</h1>
                            <p className="dr-meta">{state.board} • {state.subject} • {state.chapter}</p>
                        </div>
                        <div className="dr-doctor-shield">
                            <div className="shield-icon">⚡</div>
                            <div className="shield-text">{t('reports:reports.aiDoctor.verified', 'SkillTherapy AI VERIFIED')}</div>
                        </div>
                    </div>


                    {/* Top Row: Basic Stats & Performance Grid */}
                    <div className="dr-grid-top">
                        <div className="dr-card dr-stats-card">
                            <h3>{t('reports:reports.aiDoctor.accuracy', 'Accuracy Breakdown')}</h3>
                            <div className="dr-stats-row">
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-correct">{accuracy.correct}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.correct', 'Correct')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-wrong">{accuracy.wrong}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.wrong', 'Wrong')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-skip">{accuracy.skipped}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.testReport.skipped', 'Skipped')}</span>
                                </div>
                                <div className="dr-stat">
                                    <span className="dr-stat-val val-time">{t('reports:reports.aiDoctor.timeSeconds', { count: 29, defaultValue: '29s' })}</span>
                                    <span className="dr-stat-lbl">{t('reports:reports.aiDoctor.avgTime', 'Avg Time')}</span>
                                </div>
                            </div>
                        </div>


                        <div className="dr-card dr-difficulty-card">
                            <h3>{t('reports:reports.aiDoctor.difficulty', 'Performance by Difficulty')}</h3>
                            <div className="dr-difficulty-table">
                                <div className="dr-table-header">
                                    <span>{t('test:test.difficultyLabel', 'Level')}</span>
                                    <span>{t('reports:reports.testReport.correct', 'Correct')}</span>
                                    <span>{t('reports:reports.testReport.wrong', 'Wrong')}</span>
                                </div>

                                {['Easy', 'Medium', 'Hard'].map(level => {
                                    const stats = difficulty_performance[level.toLowerCase()] || { correct: 0, wrong: 0 };
                                    return (
                                        <div key={level} className="dr-table-row">
                                            <span className={`lbl-${level.toLowerCase()}`}>{t(`test:test.difficulty.${level.toLowerCase()}`, level)}</span>
                                            <span className="val-correct">{stats.correct}</span>
                                            <span className="val-wrong">{stats.wrong}</span>
                                        </div>
                                    );
                                })}

                            </div>
                        </div>
                    </div>

                    {/* Confidence Meter Row */}
                    <div className="dr-card dr-confidence-card-full">
                        <h3>{t('reports:reports.aiDoctor.confidence', 'AI Confidence Meter')}</h3>
                        <div className="dr-meter-container">
                            <div className="dr-meter-track">
                                <div className="dr-meter-fill" style={{ width: `${confidence_meter}%` }}></div>
                            </div>
                            <div className="dr-meter-value">{t('reports:reports.aiDoctor.confidenceVal', { percent: confidence_meter, defaultValue: `${confidence_meter}% Confidence` })}</div>
                            <p className="dr-meter-desc">{t('reports:reports.aiDoctor.confidenceDesc', 'Based on response time and pattern analysis.')}</p>
                        </div>
                    </div>


                    {/* Middle Row: Topics & Insights */}
                    <div className="dr-grid-mid">
                        <div className="dr-card dr-topics-card">
                            <h3>{t('reports:reports.aiDoctor.conceptMastery', 'Concept-Wise Mastery')}</h3>
                            <div className="dr-topics-list">

                                {topic_weakness.map((topic, i) => (
                                    <div key={i} className={`dr-topic-item status-${topic.status.replace(/\s+/g, '').toLowerCase()}`}>
                                        <span className="topic-name">{topic.topic}</span>
                                        <span className="topic-status-pill">{t(`reports:reports.aiDoctor.grades.${topic.status.replace(/\s+/g, '').toLowerCase()}`, topic.status)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>


                        <div className="dr-card dr-insights-card">
                            <h3><span className="sparkle-icon">✨</span> {t('reports:reports.aiDoctor.mistakePatterns', 'AI Mistake Patterns')}</h3>

                            <div className="dr-insights-list">
                                {ai_insights.map((insight, i) => (
                                    <div key={i} className="dr-insight-item">
                                        <p>{insight}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Mistake Reasons & Improvement Plan */}
                    <div className="dr-grid-bottom">
                        <div className="dr-card dr-reasons-card">
                            <h3>{t('reports:reports.aiDoctor.reasons', 'Reason for Mistakes')}</h3>
                            <div className="dr-reasons-grid">

                                {Object.entries(mistake_reason_analysis).map(([key, val]) => (
                                    <div key={key} className="dr-reason-box">
                                        <div className="reason-val">{val}%</div>
                                        <div className="reason-lbl">{key.replace('_', ' ')}</div>
                                        <div className="reason-bar"><div className="reason-bar-fill" style={{ width: `${val}%` }}></div></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="dr-card dr-plan-card">
                            <h3>🚀 {t('reports:reports.aiDoctor.improvementPlan', '7-Day Improvement Plan')}</h3>
                            <div className="dr-plan-timeline">

                                {seven_day_plan.map((day, i) => (
                                    <div key={i} className="dr-plan-day">
                                        <div className="plan-day-circle">{i + 1}</div>
                                        <div className="plan-day-content">
                                            <h4>{t(`common:days.day${i + 1}`, day.day)}</h4>

                                            <p>{day.task}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Action: Recommended Practice */}
                    <div className="dr-card dr-practice-card">
                        <h3>{t('reports:reports.aiDoctor.nextSteps', 'Recommended Next Steps')}</h3>
                        <div className="dr-practice-grid">

                            {recommended_practice.map((item, i) => (
                                <div key={i} className="dr-practice-item">
                                    <div className="practice-icon">🎯</div>
                                    <div className="practice-info">
                                        <h4>{item.type}</h4>
                                        <span className={`priority-pill ${item.priority.toLowerCase()}`}>
                                            {t(`common:priority.${item.priority.toLowerCase()}`, item.priority)} {t('common:priorityLabel', 'Priority')}
                                        </span>
                                    </div>
                                    <button className="dr-btn-start">{t('reports:reports.aiDoctor.startNow', 'Start Now')}</button>
                                </div>

                            ))}
                        </div>
                    </div>

                    <div className="dr-footer-actions">
                        {state.mode === 'review' ? (
                            <>
                                <button
                                    className="dr-btn-primary"
                                    onClick={handleRetake}
                                >
                                    🔄 {t('reports:reports.aiDoctor.retake', 'Retake Test')}
                                </button>
                                <button className="dr-btn-outline" onClick={() => navigate('/subjects')}>
                                    📚 {t('reports:reports.aiDoctor.backToSubjects', 'Back to Subjects')}
                                </button>

                            </>
                        ) : (
                            <>
                                <button onClick={() => navigate('/report', { state })} className="dr-btn-secondary">{t('reports:reports.aiDoctor.viewDetailed', 'View Detailed Question Review')}</button>
                                <button onClick={() => navigate('/dashboard')} className="dr-btn-outline">{t('reports:reports.aiDoctor.backToDashboard', 'Back to Dashboard')}</button>
                            </>

                        )}
                    </div>
                </div>
            </main>

            {/* Premium Upsell Modal */}
            {showExceededModal && (
                <div className="dr-modal-overlay">
                    <div className="dr-upsell-modal">
                        <div className="dr-modal-header">
                            <div className="dr-modal-icon">💎</div>
                            <h2>{t('reports:analysisReport.upsell.title', 'Chapter Limit Reached')}</h2>
                            <button className="dr-modal-close" onClick={() => setShowExceededModal(false)}>✕</button>
                        </div>
                        <div className="dr-modal-body">
                            <p>{t('reports:analysisReport.upsell.description', "You've utilized your free unique chapter quota. To continue re-taking tests and unlocking more chapters, upgrade to our Chapter Pack.")}</p>
                            <div className="dr-offer-card">
                                <div className="dr-offer-info">
                                    <span className="dr-offer-title">{t('reports:analysisReport.upsell.offer', { count: 8, defaultValue: '8 Premium Chapters' })}</span>
                                    <span className="dr-offer-price">{t('reports:analysisReport.upsell.price', { amount: 20, defaultValue: 'Only ₹20' })}</span>
                                </div>
                                <div className="dr-offer-check">✓</div>
                            </div>
                            <ul className="dr-modal-features">
                                {(t('reports:analysisReport.upsell.features', { returnObjects: true, count: 8 }) || []).map((feature, idx) => (
                                    <li key={idx}>✨ {feature}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="dr-modal-footer">
                            <button className="dr-btn-primary full-width" onClick={() => navigate('/pricing')}>
                                {t('reports:analysisReport.upsell.buyBtn', 'Buy Chapter Pack Now')}
                            </button>
                            <button className="dr-link-btn" onClick={() => setShowExceededModal(false)}>
                                {t('reports:analysisReport.upsell.maybeLater', 'Maybe later')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIDoctorReport;
